import { motion } from "framer-motion";
import { Snowflake, Flame, Wrench, Award, ArrowUpRight } from "lucide-react";
import Reveal from "./Reveal";
import { useSite } from "@/App";

const COOLING_IMG =
  "https://images.unsplash.com/photo-1698479603408-1a66a6d9e80f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25pbmd8ZW58MHx8fHwxNzg1NzczNjkzfDA&ixlib=rb-4.1.0&q=85";
const HEATING_IMG =
  "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1NzczNjkzfDA&ixlib=rb-4.1.0&q=85";

const cooling = [
  { title: "AC Repair", desc: "Fast, careful diagnosis and honest repairs that get your system running right. We fix the problem, not just the symptom." },
  { title: "AC Installation", desc: "Right-sized, professionally installed cooling systems matched to your home. Premium American Standard equipment built to last." },
  { title: "AC Maintenance", desc: "Seasonal tune-ups that protect efficiency and extend equipment life. Small attention now prevents big bills later." },
];

const heating = [
  { title: "Furnace Repair", desc: "Reliable heating repair for the coldest Central Texas nights. We show up prepared and get your warmth back quickly." },
  { title: "Heat Pump Repair", desc: "Expert service for modern heat pump systems, cooling and heating alike. Efficient comfort kept in peak condition." },
  { title: "Heating Installation & Maintenance", desc: "New furnace and heat pump installs plus preventive maintenance. Comfortable, efficient, and dependable all winter long." },
];

function ServiceCard({ item, accent, Icon, delay }) {
  return (
    <Reveal delay={delay}>
      <div
        data-testid={`service-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className="group h-full rounded-2xl bg-[#171B22] border border-[#9AA3AF]/15 p-7 transition-[transform,border-color,box-shadow] duration-400 hover:-translate-y-1.5"
        style={{ boxShadow: "0 1px 0 rgba(255,255,255,0.02)" }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(154,163,175,0.15)")}
      >
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
          style={{ background: `${accent}1a`, color: accent }}
        >
          <Icon size={22} />
        </div>
        <h4 className="text-xl text-white mb-2">{item.title}</h4>
        <p className="text-[#9AA3AF] leading-relaxed">{item.desc}</p>
        <div
          className="mt-5 h-px w-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
          style={{ background: accent }}
        />
      </div>
    </Reveal>
  );
}

export default function Services() {
  const { openQuote } = useSite();
  return (
    <section id="services" className="relative bg-[#171B22] py-24 md:py-32">
      <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <img src="/markonlyvector.png" alt="" className="h-6 w-auto opacity-80" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#9AA3AF]">Our Services</span>
          </div>
          <h2 className="text-3xl md:text-5xl text-[#C9A44C] max-w-3xl">
            Complete cooling and heating, handled with care.
          </h2>
          <p className="mt-5 max-w-2xl text-white/80 text-lg">
            These are our core categories and they are easy to expand over time. Whatever the
            season, we keep your comfort steady.
          </p>
        </Reveal>

        {/* Cooling */}
        <div id="cooling" className="mt-16 scroll-mt-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl mb-8">
              <img src={COOLING_IMG} alt="Air conditioning" className="w-full h-52 md:h-64 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#101317] via-[#101317]/70 to-[#005EA8]/40" />
              <div className="absolute inset-0 flex items-center px-8">
                <div className="flex items-center gap-3">
                  <Snowflake style={{ color: "#4aa3e0" }} size={28} />
                  <h3 className="text-2xl md:text-3xl text-white">Cooling</h3>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cooling.map((c, i) => (
              <ServiceCard key={c.title} item={c} accent="#005EA8" Icon={i === 2 ? Wrench : Snowflake} delay={i * 0.08} />
            ))}
          </div>
        </div>

        {/* Heating */}
        <div id="heating" className="mt-20 scroll-mt-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl mb-8">
              <img src={HEATING_IMG} alt="Heating" className="w-full h-52 md:h-64 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#101317] via-[#101317]/70 to-[#C3262E]/40" />
              <div className="absolute inset-0 flex items-center px-8">
                <div className="flex items-center gap-3">
                  <Flame style={{ color: "#e0644a" }} size={28} />
                  <h3 className="text-2xl md:text-3xl text-white">Heating</h3>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {heating.map((c, i) => (
              <ServiceCard key={c.title} item={c} accent="#C3262E" Icon={i === 2 ? Wrench : Flame} delay={i * 0.08} />
            ))}
          </div>
        </div>

        {/* American Standard Dealer highlight */}
        <Reveal delay={0.1}>
          <div className="mt-16 relative overflow-hidden rounded-3xl border border-[#C9A44C]/30 bg-gradient-to-br from-[#101317] to-[#171B22] p-8 md:p-12">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#C9A44C]/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-8 justify-between">
              <div className="flex items-start gap-5 max-w-2xl">
                <div className="w-14 h-14 rounded-2xl bg-[#C9A44C]/15 text-[#C9A44C] flex items-center justify-center shrink-0">
                  <Award size={26} />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl text-[#C9A44C]">American Standard Dealer</h3>
                  <p className="mt-3 text-white/85 text-lg leading-relaxed">
                    We install and service premium American Standard systems, pairing dependable,
                    high-efficiency equipment with expert workmanship. It is comfort you can rely on,
                    backed by a name you can trust.
                  </p>
                </div>
              </div>
              <button
                data-testid="services-quote-btn"
                onClick={openQuote}
                className="focus-gold shrink-0 inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold bg-[#C9A44C] text-[#171B22] hover:bg-[#D6B15A] transition-colors duration-300"
              >
                Request a Quote <ArrowUpRight size={18} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
